import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText } from 'lucide-react';
import type { Artifact } from '@/types';

interface PlanArtifactProps {
  artifact: Artifact;
}

interface PlanSummary {
  stageCount?: number;
  actionCount?: number;
  estimatedApiCalls?: number;
  planId?: string;
  sessionId?: string;
}

export function PlanArtifact({ artifact }: PlanArtifactProps) {
  // Try to parse the content as JSON plan summary
  let planSummary: PlanSummary | null = null;
  try {
    if (artifact.content.startsWith('{')) {
      planSummary = JSON.parse(artifact.content);
    }
  } catch {
    // Not JSON, render as markdown
  }

  // If we have a JSON plan summary, render it as an interactive card
  if (planSummary) {
    return (
      <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-purple-100 dark:bg-purple-800 p-2">
            <FileText className="h-5 w-5 text-purple-600 dark:text-purple-300" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-purple-900 dark:text-purple-100">
              {artifact.title ?? 'Implementation Plan'}
            </h3>
            <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-wide">
                  Stages
                </div>
                <div className="font-semibold text-purple-900 dark:text-purple-100">
                  {planSummary.stageCount ?? 1}
                </div>
              </div>
              <div>
                <div className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-wide">
                  Actions
                </div>
                <div className="font-semibold text-purple-900 dark:text-purple-100">
                  {planSummary.actionCount ?? 0}
                </div>
              </div>
              <div>
                <div className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-wide">
                  API Calls
                </div>
                <div className="font-semibold text-purple-900 dark:text-purple-100">
                  ~{planSummary.estimatedApiCalls ?? 0}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // Otherwise render as markdown
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="mb-2 font-medium text-gray-900 dark:text-gray-100">
        {artifact.title ?? 'Implementation Plan'}
      </h3>
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {artifact.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}
